import { Box, Typography, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import type { MetricResult } from '../../types';

type Props = {
  trend: NonNullable<MetricResult['trend']>;
  pill?: boolean;
  /** Default small; hero uses medium. */
  size?: 'small' | 'medium';
};

export default function TrendBadge({ trend, pill, size = 'small' }: Props) {
  const theme = useTheme();
  const color =
    trend.sentiment === 'good'
      ? theme.palette.success.main
      : trend.sentiment === 'bad'
        ? theme.palette.error.main
        : theme.palette.text.secondary;
  const Icon = trend.direction === 'up' ? TrendingUpIcon : TrendingDownIcon;
  const isMedium = size === 'medium';
  // Label may already include ↑/↓ — TrendBadge icon covers direction, so strip duplicates.
  const label = trend.label.replace(/^[↑↓]\s*/, '').trim();

  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: isMedium ? 0.75 : 0.5,
        color,
        ...(pill
          ? {
              px: isMedium ? 2 : 1.5,
              py: isMedium ? 0.75 : 0.5,
              borderRadius: 10,
              backgroundColor: alpha(color, theme.palette.mode === 'dark' ? 0.14 : 0.1),
              border: `1px solid ${color}`,
            }
          : {}),
      }}
    >
      {trend.direction !== 'flat' ? (
        <Icon sx={{ fontSize: isMedium ? 22 : 16 }} />
      ) : null}
      <Typography
        variant={isMedium ? 'body2' : 'caption'}
        sx={{ fontWeight: 600, color, fontSize: isMedium ? '0.95rem' : undefined }}
      >
        {label}
      </Typography>
    </Box>
  );
}
